import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { Button, Divider, Sidebar, Toast, type ToastRef, Tooltip } from '@allygpo/ally-ux';

import allyIqIcon from '../../assets/allyiq-icon.svg';
import allyIqLogo from '../../assets/allyiq-logo.svg';
import { getAllyIqUrl } from '../../authConfig';
import { ConfirmHost } from '../ui/confirm';
import { ToastContext, createAppToast, type AppToast } from '../../context/ToastContext';
import { usePracticeData } from '../../hooks/usePracticeData';
import { NAV_ITEMS, NavItem, sectionFor } from './navItems';
import NavFlyout from './NavFlyout';
import PracticePicker from './PracticePicker';
import './Layout.css';

interface LayoutProps { children: React.ReactNode; onLogout: () => void; }

const RailIcon: React.FC<{ path: string }> = ({ path }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={path} /></svg>
);

const CHEVRON_PATH = 'M9 6l6 6-6 6';
// Grace period so the pointer can cross from a rail button to its pop-out without it closing.
const FLYOUT_CLOSE_DELAY_MS = 180;

const COLLAPSED_KEY = 'siq-nav-collapsed';

// Remembered per browser; storage can be unavailable (private mode, blocked site data), so fall back to expanded.
const readCollapsed = (): boolean => { try { return localStorage.getItem(COLLAPSED_KEY) === '1'; } catch { return false; } };
const writeCollapsed = (collapsed: boolean): void => { try { localStorage.setItem(COLLAPSED_KEY, collapsed ? '1' : '0'); } catch { /* not persisted */ } };

/** App shell: collapsible left nav, top bar (product name, practice picker, signed-in user), scrolling main area. */
const Layout: React.FC<LayoutProps> = ({ children, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { accounts } = useMsal();
  const [isMobileNavOpen, setMobileNavOpen] = useState(false);
  const [isCollapsed, setCollapsed] = useState(readCollapsed);
  const [allyIqUrl, setAllyIqUrl] = useState('');
  useEffect(() => { getAllyIqUrl().then(setAllyIqUrl).catch(() => setAllyIqUrl('')); }, []);
  const toggleCollapsed = () => setCollapsed((prev) => { writeCollapsed(!prev); return !prev; });
  const allyToastRef = useRef<ToastRef>(null);
  const toastRef = useRef<AppToast>(createAppToast(allyToastRef));
  const activeSection = sectionFor(location.pathname);

  // Pop-out menu for a section's sub-pages, anchored to its rail button (AllyIQ pattern).
  const [flyout, setFlyout] = useState<{ section: NavItem; top: number; left: number; focusFirst: boolean } | null>(null);
  const railButtons = useRef<Record<string, HTMLButtonElement | null>>({});
  const closeTimer = useRef<number | undefined>(undefined);
  const cancelClose = () => window.clearTimeout(closeTimer.current);
  const flyoutRef = useRef(flyout);
  flyoutRef.current = flyout;
  const closeFlyout = useCallback((returnFocus = false) => {
    window.clearTimeout(closeTimer.current);
    const current = flyoutRef.current;
    setFlyout(null);
    if (current && returnFocus) railButtons.current[current.section.label]?.focus();
  }, []);
  const scheduleClose = () => { cancelClose(); closeTimer.current = window.setTimeout(() => closeFlyout(), FLYOUT_CLOSE_DELAY_MS); };
  const openFlyout = (section: NavItem, button: HTMLElement, focusFirst: boolean) => {
    cancelClose();
    const rect = button.getBoundingClientRect();
    setFlyout({ section, top: rect.top, left: rect.right + 4, focusFirst });
  };
  // Close on navigation, resize, or a press anywhere outside the menu and its rail button.
  useEffect(() => closeFlyout(), [location.pathname, isCollapsed, closeFlyout]);
  // Pages share one scrolling work area, so a new page would open at the last page's scroll position
  // (e.g. Results opening near the bottom after Generate on New run). Start each page at the top.
  const workRef = useRef<HTMLElement>(null);
  useEffect(() => { workRef.current?.scrollTo({ top: 0 }); }, [location.pathname]);
  useEffect(() => {
    if (!flyout) return undefined;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (document.getElementById('siq-nav-flyout')?.contains(target) || railButtons.current[flyout.section.label]?.contains(target)) return;
      closeFlyout();
    };
    const onResize = () => closeFlyout();
    document.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('resize', onResize);
    return () => { document.removeEventListener('pointerdown', onPointerDown); window.removeEventListener('resize', onResize); };
  }, [flyout, closeFlyout]);
  useEffect(() => () => window.clearTimeout(closeTimer.current), []);

  const go = (path: string) => { navigate(path); setMobileNavOpen(false); closeFlyout(); };
  const { userDisplayName, userEmail } = usePracticeData();
  // The backend resolves first/last name and the token's email claims; the MSAL account
  // for this tenant carries only a display name (surname first), so it is the fallback.
  const userName = userDisplayName || accounts[0]?.name || '';

  const logo = isCollapsed
    ? <img src={allyIqIcon} alt="AllyIQ" className="siq-rail-logo-icon" />
    : <img src={allyIqLogo} alt="AllyIQ" className="siq-rail-logo-wordmark h-2rem w-auto block" />;

  return <div className={`siq-shell${isCollapsed ? '' : ' is-expanded'}`}>
    <nav className="siq-rail" aria-label="Primary">
      {allyIqUrl ? <a className="siq-rail-mark" href={allyIqUrl} target="_blank" rel="noopener noreferrer" aria-label="Open AllyIQ in a new window">{logo}</a> : <div className="siq-rail-mark">{logo}</div>}
      <ul className="siq-rail-items list-none u-m-0 u-p-0 flex flex-column">{NAV_ITEMS.map((item) => {
        const active = item === activeSection;
        if (!item.children) {
          return <li key={item.to}><button type="button" className={`siq-rail-item${active ? ' is-active' : ''}`} onClick={() => go(item.to)} aria-current={active ? 'page' : undefined}><RailIcon path={item.path} /><span>{item.label}</span></button></li>;
        }
        // Sections with sub-pages open a pop-out on hover, click or ArrowRight instead of navigating.
        const open = flyout?.section === item;
        return <li key={item.to}><button type="button" ref={(el) => { railButtons.current[item.label] = el; }}
          className={`siq-rail-item has-menu${active ? ' is-active' : ''}${open ? ' is-open' : ''}`}
          aria-haspopup="menu" aria-expanded={open} aria-controls={open ? 'siq-nav-flyout' : undefined}
          // Hover only for a real mouse: a tap also fires pointerenter, then a click that would toggle it shut.
          onPointerEnter={(e) => { if (e.pointerType === 'mouse') openFlyout(item, e.currentTarget, false); }}
          onPointerLeave={(e) => { if (e.pointerType === 'mouse') scheduleClose(); }}
          // detail is 0 when the click came from the keyboard (Enter/Space): focus the first item then.
          onClick={(e) => (open && e.detail !== 0 ? closeFlyout() : openFlyout(item, e.currentTarget, e.detail === 0))}
          onKeyDown={(e) => { if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); openFlyout(item, e.currentTarget, true); } }}>
          <span className="siq-rail-icon"><RailIcon path={item.path} /><svg className="siq-rail-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={CHEVRON_PATH} /></svg></span>
          <span>{item.label}</span>
        </button></li>;
      })}</ul>
    </nav>
    <button type="button" className="siq-rail-toggle" onClick={toggleCollapsed} aria-expanded={!isCollapsed} aria-label={isCollapsed ? 'Expand navigation' : 'Collapse navigation'}>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={isCollapsed ? 'M9 5l7 7-7 7' : 'M15 5l-7 7 7 7'} /></svg>
    </button>
    <div className="siq-main">
      <header className="siq-topbar">
        <Button variant="primary-text" className="siq-mobile-nav-toggle" onClick={() => setMobileNavOpen(true)} aria-label="Open navigation"><i className="pi pi-bars" aria-hidden="true" /></Button>
        <div className="flex align-items-center u-gap-4 flex-none"><span className="siq-brand-name text-md font-bold tracking-tight text-selected white-space-nowrap">Scenario<span className="text-default">IQ</span></span><Divider orientation="vertical" className="u-m-0 h-1rem" /></div>
        <PracticePicker />
        <span className="flex-auto" />
        {(userName || userEmail) && <div className="flex flex-column align-items-end flex-none">
          {userName && <span className="text-md font-bold text-default white-space-nowrap">{userName}</span>}
          {userEmail && <span className="siq-user-email text-xs text-tertiary white-space-nowrap">{userEmail}</span>}
        </div>}
        {/* Sign out sits at the top bar's end, as in AllyIQ */}
        <Tooltip target=".siq-signout" position="bottom" />
        <Button variant="primary-text" className="siq-signout" onClick={onLogout} aria-label="Sign out" data-pr-tooltip="Sign out"><i className="pi pi-sign-out text-md" aria-hidden="true" /></Button>
      </header>
      <main ref={workRef} className="siq-work"><ToastContext.Provider value={toastRef}>{children}</ToastContext.Provider></main>
    </div>
    <Sidebar visible={isMobileNavOpen} onHide={() => setMobileNavOpen(false)} position="left" dismissableMask className="siq-mobile-nav"><div className="flex flex-column u-gap-1 u-p-4"><h2 className="siq-mobile-nav-title u-mt-0 u-mx-0 u-mb-3 text-sm font-bold text-selected">Scenario<span className="text-default">IQ</span></h2>{NAV_ITEMS.map((item) => <React.Fragment key={item.label}>
      <Button variant="primary-text" className={`justify-content-start${item === activeSection && !item.children ? ' is-active' : ''}`} onClick={() => go(item.to)}>{item.icon && <i className={item.icon} aria-hidden="true" />}{item.label}</Button>
      {item.children?.map((child) => <Button key={child.to} variant="primary-text" className={`justify-content-start siq-mobile-nav-sub u-pl-10${child.to === location.pathname ? ' is-active' : ''}`} onClick={() => go(child.to)}>{child.label}</Button>)}
    </React.Fragment>)}<Button variant="primary-text" className="justify-content-start" onClick={onLogout}><i className="pi pi-sign-out text-md" aria-hidden="true" />Sign out</Button></div></Sidebar>
    {flyout?.section.children && <NavFlyout id="siq-nav-flyout" section={flyout.section.label} items={flyout.section.children}
      activePath={location.pathname} anchor={flyout} focusFirst={flyout.focusFirst} onNavigate={go} onClose={closeFlyout}
      onPointerEnter={cancelClose} onPointerLeave={scheduleClose} />}
    <Toast ref={allyToastRef} position="bottom-right" />
    <ConfirmHost />
  </div>;
};

export default Layout;
